/**
 * Avondale HHA/PCA Handbook acknowledgment contract shared by the portal client and server.
 *
 * The handbook PDF is stored as a blob-backed PdfTemplate and served through
 * `GET /api/doc/avondale-handbook/document`. Bump `version` and replace the template when
 * Avondale publishes a new handbook so the stamped Nexus record reflects which edition
 * the caregiver acknowledged.
 */
export const AVONDALE_HANDBOOK = {
  title: 'HHA/PCA Handbook',
  version: 'May 2026',
  organization: 'Avondale Care Group',
  templateName: 'Avondale HHA-PCA Handbook',
  templateFormName: 'standalone-document',
  templateLanguage: 'English',
  pageCount: 105,
} as const;

export const AVONDALE_HANDBOOK_ROUTE = '/avondale-handbook';
export const AVONDALE_HANDBOOK_DOCUMENT_ENDPOINT = '/api/doc/avondale-handbook/document';
export const AVONDALE_HANDBOOK_ACKNOWLEDGE_ENDPOINT = '/api/doc/avondale-handbook/acknowledge';
/** `GET {endpoint}/:nexusId` resolves the parameterized link against Avondale Nexus. */
export const AVONDALE_HANDBOOK_CAREGIVER_ENDPOINT = '/api/doc/avondale-handbook/caregiver';

export const AVONDALE_HANDBOOK_ATTESTATION =
  'I acknowledge that I have received access to the HHA/PCA Handbook and that my electronic signature below '
  + 'is the legal equivalent of my handwritten signature.';

export interface AvondaleHandbookCaregiverResponse {
  nexusId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  /** Opaque revision of the exact handbook blob this caregiver is being asked to sign. */
  handbookRevision: string;
  /** Date (YYYY-MM-DD) already stamped on the Nexus record, if the caregiver signed before. */
  previousAcknowledgedDate: string | null;
  /** True when the signing form must collect an email address for delivery and Nexus write-back. */
  emailRequired: boolean;
}

export interface AvondaleHandbookAcknowledgeRequest {
  nexusId: string;
  handbookRevision: string;
  signature: string;
  attested: boolean;
  readingSeconds: number;
  email?: string;
}

export interface AvondaleHandbookAcknowledgeResponse {
  message: string;
  success: boolean;
  nexusStamped: boolean;
  nexusWarning?: string;
  emailSent?: boolean;
  emailWarning?: string;
  acknowledgedDate: string;
  pdfBase64: string;
}
